import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    ResponsiveContainer as RechartsResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
} from "recharts";

// Recharts' ResponsiveContainer logs noisy `width(-1)/height(-1)` warnings on
// the first mount because its parent's ResizeObserver hasn't fired yet (very
// common with React StrictMode's double-render). Passing explicit minHeight
// and a width fallback silences the warning while keeping the chart fully
// responsive once layout settles.
function ResponsiveContainer({ children }) {
    return (
        <RechartsResponsiveContainer width="100%" height="100%" minHeight={240} minWidth={0}>
            {children}
        </RechartsResponsiveContainer>
    );
}
import { ChartLine, FunnelSimple, Wallet, Receipt, Users, ChatCircleDots, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

// Brand palette — keeps every chart visually consistent.
const PRIMARY = "#0066D9";
const NAVY = "#003366";
const ACCENT = "#E6002C";
const STATUS_COLORS = {
    pending: "#F59E0B",     // amber
    in_review: "#0066D9",   // primary blue
    approved: "#10B981",    // emerald
    rejected: "#E6002C",    // accent red
    completed: "#003366",   // navy
};
const STATUS_LABELS = {
    pending: "Pending",
    in_review: "In Review",
    approved: "Approved",
    rejected: "Rejected",
    completed: "Completed",
};

function fmtMoney(n) {
    return `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// Branded tooltip — Recharts default is generic; this matches our UI.
function TooltipBox({ active, payload, label, formatter }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-md border border-[#E2E8F0] bg-white shadow-md px-3 py-2 text-xs">
            {label !== undefined && (
                <div className="font-semibold text-[#003366] mb-1">{label}</div>
            )}
            {payload.map((p) => (
                <div key={p.dataKey || p.name} className="flex items-center gap-2 text-slate-700">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
                    <span>{p.name}:</span>
                    <span className="font-semibold text-[#003366]">
                        {formatter ? formatter(p.value) : p.value}
                    </span>
                </div>
            ))}
        </div>
    );
}

function ChartCard({ title, subtitle, children, empty, testid }) {
    return (
        <Card className="border-[#E2E8F0]" data-testid={testid}>
            <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col gap-0.5 mb-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                        {title}
                    </div>
                    {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
                </div>
                {empty ? (
                    <div className="h-64 flex items-center justify-center text-sm text-slate-400">
                        No data for the selected filters.
                    </div>
                ) : (
                    <div className="h-64">{children}</div>
                )}
            </CardContent>
        </Card>
    );
}

// Compact summary card — sits in the metrics strip above the charts.
function SummaryCard({ label, value, icon: Icon, accent, testid }) {
    return (
        <Card className="border-[#E2E8F0]" data-testid={testid}>
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
                        <Icon size={18} weight="bold" className="text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold truncate">
                            {label}
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-[#003366] leading-tight" style={{ fontFamily: "Outfit" }}>
                            {value}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdminCharts() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sites, setSites] = useState([]);
    const [technicians, setTechnicians] = useState([]);

    // Filters — sane defaults so the page is useful on first load.
    const [weekStartGte, setWeekStartGte] = useState("");
    const [weekEndLte, setWeekEndLte] = useState("");
    const [siteFilter, setSiteFilter] = useState("all");
    const [techFilter, setTechFilter] = useState("all");

    // Load sites + technicians (once).
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/sites").catch(() => ({ data: [] })),
            api.get("/users", { params: { role: "technician" } }).catch(() => ({ data: [] })),
        ]).then(([s, t]) => {
            if (cancelled) return;
            setSites(s.data || []);
            setTechnicians(t.data || []);
        });
        return () => { cancelled = true; };
    }, []);

    // Re-fetch chart data whenever any filter changes.
    useEffect(() => {
        let cancelled = false;
        const params = {};
        if (weekStartGte) params.week_start_gte = weekStartGte;
        if (weekEndLte) params.week_end_lte = weekEndLte;
        if (siteFilter !== "all") params.site = siteFilter;
        if (techFilter !== "all") params.technician_id = techFilter;
        setLoading(true);
        api.get("/admin/charts", { params })
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch((e) => { if (!cancelled) toast.error(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [weekStartGte, weekEndLte, siteFilter, techFilter]);

    const activeSites = useMemo(() => sites.filter((s) => s.active), [sites]);
    const filtersActive = weekStartGte || weekEndLte || siteFilter !== "all" || techFilter !== "all";

    const weekly = data?.weekly || [];
    const requestsByStatus = (data?.requests_by_status || []).map((r) => ({
        ...r,
        label: STATUS_LABELS[r.status] || r.status,
    }));
    const requestsTotal = requestsByStatus.reduce((s, r) => s + r.count, 0);
    const payrollBySite = data?.payroll_by_site || [];

    const clearFilters = () => {
        setWeekStartGte("");
        setWeekEndLte("");
        setSiteFilter("all");
        setTechFilter("all");
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Insights
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Charts &amp; Trends
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm max-w-2xl">
                        Track payroll volume, report flow, request mix, and overtime by week.
                        Filter by date range, site, or technician to drill in.
                    </p>
                </div>
                <ChartLine size={36} weight="duotone" className="text-[#0066D9] hidden sm:block" />
            </div>

            {/* Filter bar */}
            <Card className="border-[#E2E8F0]" data-testid="charts-filter-card">
                <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-3 text-[#003366] font-semibold text-sm" style={{ fontFamily: "Outfit" }}>
                        <FunnelSimple size={16} weight="bold" /> Filters
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Week start ≥
                            </Label>
                            <Input
                                type="date"
                                value={weekStartGte}
                                onChange={(e) => setWeekStartGte(e.target.value)}
                                className="h-10 mt-1"
                                data-testid="charts-filter-week-start"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Week end ≤
                            </Label>
                            <Input
                                type="date"
                                value={weekEndLte}
                                onChange={(e) => setWeekEndLte(e.target.value)}
                                className="h-10 mt-1"
                                data-testid="charts-filter-week-end"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Site
                            </Label>
                            <Select value={siteFilter} onValueChange={setSiteFilter}>
                                <SelectTrigger className="h-10 mt-1" data-testid="charts-filter-site">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All sites</SelectItem>
                                    {activeSites.map((s) => (
                                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Technician
                            </Label>
                            <Select value={techFilter} onValueChange={setTechFilter}>
                                <SelectTrigger className="h-10 mt-1" data-testid="charts-filter-tech">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All technicians</SelectItem>
                                    {technicians.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="outline"
                                onClick={clearFilters}
                                disabled={!filtersActive}
                                className="h-10 w-full"
                                data-testid="charts-clear-filters"
                            >
                                Clear filters
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading && !data ? (
                <div className="py-16 text-center text-slate-500">Loading charts…</div>
            ) : (
                <>
                    {/* Summary metrics — reflect the same filtered window as the charts */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="charts-summary-cards">
                        <SummaryCard
                            label="Total Paid"
                            value={`$${Number(data?.summary?.total_paid ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                            icon={Wallet}
                            accent="bg-[#0066D9]"
                            testid="summary-total-paid"
                        />
                        <SummaryCard
                            label="Total Reports"
                            value={data?.summary?.total_reports ?? 0}
                            icon={Receipt}
                            accent="bg-[#003366]"
                            testid="summary-total-reports"
                        />
                        <SummaryCard
                            label="Active Technicians"
                            value={data?.summary?.active_technicians ?? 0}
                            icon={Users}
                            accent="bg-slate-700"
                            testid="summary-active-technicians"
                        />
                        <SummaryCard
                            label="Open Requests"
                            value={data?.summary?.open_requests ?? 0}
                            icon={ChatCircleDots}
                            accent="bg-[#E6002C]"
                            testid="summary-open-requests"
                        />
                        <SummaryCard
                            label="Overtime Hours"
                            value={`${Number(data?.summary?.overtime_hours ?? 0).toLocaleString("en-US", { maximumFractionDigits: 1 })}h`}
                            icon={Clock}
                            accent="bg-amber-500"
                            testid="summary-overtime-hours"
                        />
                    </div>

                    {/* Charts grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <ChartCard
                            title="Weekly Payroll Total"
                            subtitle="Sum of total pay per week"
                            empty={weekly.length === 0}
                            testid="chart-weekly-payroll"
                        >
                            <ResponsiveContainer>
                                <LineChart data={weekly} margin={{ top: 10, right: 16, left: 6, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="week_start" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                                    <Tooltip content={<TooltipBox formatter={fmtMoney} />} />
                                    <Line type="monotone" dataKey="total_paid" name="Payroll" stroke={PRIMARY} strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard
                            title="Reports Created Per Week"
                            subtitle="Number of payment reports per week"
                            empty={weekly.length === 0}
                            testid="chart-reports-per-week"
                        >
                            <ResponsiveContainer>
                                <BarChart data={weekly} margin={{ top: 10, right: 16, left: 6, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="week_start" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip content={<TooltipBox />} />
                                    <Bar dataKey="report_count" name="Reports" fill={NAVY} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard
                            title="Requests by Status"
                            subtitle={`${requestsTotal} request${requestsTotal === 1 ? "" : "s"} total`}
                            empty={requestsTotal === 0}
                            testid="chart-requests-by-status"
                        >
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={requestsByStatus.filter((r) => r.count > 0)}
                                        dataKey="count"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                    >
                                        {requestsByStatus.filter((r) => r.count > 0).map((r) => (
                                            <Cell key={r.status} fill={STATUS_COLORS[r.status] || "#64748B"} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<TooltipBox />} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        wrapperStyle={{ fontSize: 11, color: "#475569" }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard
                            title="Payroll by Site"
                            subtitle="Total paid per site (filtered range)"
                            empty={payrollBySite.length === 0}
                            testid="chart-payroll-by-site"
                        >
                            <ResponsiveContainer>
                                <BarChart data={payrollBySite} layout="vertical" margin={{ top: 10, right: 16, left: 6, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                                    <YAxis type="category" dataKey="site" stroke="#94A3B8" tick={{ fontSize: 11 }} width={80} />
                                    <Tooltip content={<TooltipBox formatter={fmtMoney} />} />
                                    <Bar dataKey="total_paid" name="Payroll" fill={PRIMARY} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard
                            title="Overtime Hours by Week"
                            subtitle="Sum of OT hours per week"
                            empty={weekly.length === 0}
                            testid="chart-overtime-by-week"
                        >
                            <ResponsiveContainer>
                                <BarChart data={weekly} margin={{ top: 10, right: 16, left: 6, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="week_start" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
                                    <Tooltip content={<TooltipBox formatter={(v) => `${v} h`} />} />
                                    <Bar dataKey="overtime_hours" name="OT hours" fill={ACCENT} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>
                </>
            )}
        </div>
    );
}
